import {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  TextField,
  MenuItem,
  Select,
  Button,
  FormControlLabel,
  Box,
  Tab,
  Tabs,
  Checkbox,
  CardMedia,
  IconButton,
  Input,
  FormHelperText,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  useAddStoreMutation,
  useGetStoreByIdQuery,
  useUpdateStoreMutation,
} from "../../services/store";
import { UploadMedia } from "../../utils/uploadMedia";
import usePermission from "../../hooks/userAccess";
import { showError } from "../../helpers";
import SwitchToggle from "../../components/SwitchToggle";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

type DayOfWeek =
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
  | "Sunday";

interface Availability {
  enabled: boolean;
  start: string;
  end: string;
  is24_7: boolean;
}

const AddStore = () => {
  const navigate = useNavigate();

  const { isAdd } = usePermission({
    label: "Store Management",
    page_permission: "isAdd",
  });

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const [valueTabs, setValueTabs] = useState(0);
  const [image, setImage] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [isPlacesLoaded, setIsPlacesLoaded] = useState(false);
  const addressInputRef = useRef<HTMLInputElement | null>(null);
  const setFieldValueRef = useRef<((field: string, value: any) => void) | null>(
    null
  );
  const autocompleteRef = useRef<any>(null);

  const { id: routeId } = useParams();
  const isEdit = Boolean(routeId);

  const { data: storeByIdRes, isFetching: isFetchingStore } =
    useGetStoreByIdQuery(
      isEdit ? { id: String(routeId) } : ({ id: "" } as any),
      { skip: !isEdit }
    );
  const editingData = useMemo(
    () => (storeByIdRes as any)?.data || null,
    [storeByIdRes]
  );

  const [addStore, { isLoading: isSaving }] = useAddStoreMutation();
  const [updateStore, { isLoading: isUpdating }] = useUpdateStoreMutation();
  const [is24_7, setIs24_7] = useState(false);
  const [availability, setAvailability] = useState<
    Record<DayOfWeek, Availability>
  >({
    Monday: { enabled: true, start: "09:00", end: "18:00", is24_7: false },
    Tuesday: { enabled: true, start: "09:00", end: "18:00", is24_7: false },
    Wednesday: { enabled: true, start: "09:00", end: "18:00", is24_7: false },
    Thursday: { enabled: true, start: "09:00", end: "18:00", is24_7: false },
    Friday: { enabled: true, start: "09:00", end: "18:00", is24_7: false },
    Saturday: { enabled: false, start: "09:00", end: "18:00", is24_7: false },
    Sunday: { enabled: false, start: "09:00", end: "18:00", is24_7: false },
  });

  const toggleAvailability = (day: DayOfWeek) => {
    setAvailability((prev) => ({
      ...prev,
      [day]: { ...prev[day], enabled: !prev[day].enabled },
    }));
  };

  const toggle24Hours = (checked: boolean) => {
    setIs24_7(checked);
  };

  const updateTime = (
    day: DayOfWeek,
    field: "start" | "end",
    value: string
  ) => {
    setAvailability((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  const initialValues = useMemo(
    () => ({
      image: editingData?.image || "",
      name: editingData?.name || "",
      name_ar: editingData?.name_ar || "",
      storeCode: editingData?.storeCode || "",
      status: typeof editingData?.status === "number" ? editingData?.status : 1,
      address: editingData?.address || "",
      city: editingData?.city || "",
      latitude: (editingData?.latitude ?? "") as any,
      longitude: (editingData?.longitude ?? "") as any,
      orderOnlineLink: editingData?.orderOnlineLink || "",
      storeManagerContact: editingData?.storeManagerContact || "",
    }),
    [editingData]
  );

  const validationSchema = Yup.object({
    image: Yup.string().required("Image is required"),
    name: Yup.string()
      .required("Store name is required")
      .min(2, "Store name must be at least 2 characters")
      .max(100, "Store name must be less than 100 characters"),
    name_ar: Yup.string()
      .required("Arabic store name is required")
      .min(2, "Arabic store name must be at least 2 characters")
      .max(100, "Arabic store name must be less than 100 characters"),
    storeCode: Yup.string()
      .required("Store code is required")
      .matches(
        /^[A-Z0-9]+$/,
        "Store code must contain only uppercase letters and numbers"
      )
      .min(3, "Store code must be at least 3 characters")
      .max(50, "Store code must be less than 50 characters"),
    status: Yup.number()
      .oneOf([0, 1], "Status must be either 0 or 1")
      .required("Status is required"),
    address: Yup.string()
      .required("Address is required")
      .min(10, "Address must be at least 10 characters")
      .max(300, "Address must be less than 300 characters"),
    city: Yup.string()
      .required("City is required")
      .min(2, "City must be at least 2 characters")
      .max(100, "City must be less than 100 characters"),
    orderOnlineLink: Yup.string()
      .url("Enter a valid URL (e.g., https://example.com)")
      .nullable()
      .optional(),
    storeManagerContact: Yup.string()
      .matches(/^[\+]?[1-9][\d]{0,15}$/, "Enter a valid phone number")
      .max(20, "Contact number must be less than 20 characters")
      .nullable()
      .optional(),
    latitude: Yup.number()
      .required("Latitude is required")
      .min(-90, "Latitude must be between -90 and 90")
      .max(90, "Latitude must be between -90 and 90"),
    longitude: Yup.number()
      .required("Longitude is required")
      .min(-180, "Longitude must be between -180 and 180")
      .max(180, "Longitude must be between -180 and 180"),
  });

  const timeToMinutes = (hhmm: string): number => {
    const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
    if (Number.isNaN(h) || Number.isNaN(m)) return 0;
    return h * 60 + m;
  };

  const minutesToHHMM = (minutes: number): string => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  // Load Google Places script
  useEffect(() => {
    if (typeof window === "undefined") return;

    const win = window as any;
    if (win.google && win.google.maps && win.google.maps.places) {
      setIsPlacesLoaded(true);
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src^="https://maps.googleapis.com/maps/api/js"]'
    );

    const handleOnLoad = () => {
      setIsPlacesLoaded(true);
    };

    if (existingScript) {
      if ((existingScript as any)._loaded) {
        setIsPlacesLoaded(true);
      } else {
        existingScript.addEventListener("load", handleOnLoad);
      }
      return () => existingScript.removeEventListener("load", handleOnLoad);
    }
    const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY!;
    if (!apiKey) {
      console.warn(
        "Google Places API key is missing in environment variables."
      );
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    (script as any)._loaded = false;
    script.onload = () => {
      (script as any)._loaded = true;
      handleOnLoad();
    };
    document.head.appendChild(script);

    return () => {
      script.removeEventListener("load", handleOnLoad);
    };
  }, []);

  // Initialize Places Autocomplete on the address field
  useEffect(() => {
    if (!isPlacesLoaded || !addressInputRef.current) return;

    const win = window as any;
    if (!win.google || !win.google.maps || !win.google.maps.places) return;

    const autocomplete = new win.google.maps.places.Autocomplete(
      addressInputRef.current,
      {
        types: ["geocode"],
      }
    );

    // Restrict the fields returned to the ones we care about
    if (autocomplete.setFields) {
      autocomplete.setFields([
        "formatted_address",
        "address_components",
        "geometry",
      ]);
    }

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place || !place.geometry || !place.geometry.location) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      let city = "";
      const components = place.address_components || [];
      const cityComponent =
        components.find((c: any) => c.types?.includes("locality")) ||
        components.find((c: any) =>
          c.types?.includes("administrative_area_level_2")
        ) ||
        components.find((c: any) =>
          c.types?.includes("administrative_area_level_1")
        );

      if (cityComponent) {
        city = cityComponent.long_name || cityComponent.short_name || "";
      }

      const setFieldValue = setFieldValueRef.current;
      if (setFieldValue) {
        setFieldValue("address", place.formatted_address || "");
        setFieldValue("city", city);
        setFieldValue("latitude", lat);
        setFieldValue("longitude", lng);
        setUserLocation({ lat, lng });
      }
    });

    autocompleteRef.current = autocomplete;

    return () => {
      if (autocompleteRef.current && autocompleteRef.current.unbindAll) {
        autocompleteRef.current.unbindAll();
      }
      autocompleteRef.current = null;
    };
  }, [isPlacesLoaded]);

  useEffect(() => {
    if (editingData) {
      if (editingData?.image) {
        setImage(editingData.image);
      }
      if (Array.isArray(editingData?.availability)) {
        const next: Record<DayOfWeek, Availability> = {
          Monday: { enabled: false, start: "09:00", end: "18:00", is24_7: false },
          Tuesday: { enabled: false, start: "09:00", end: "18:00", is24_7: false },
          Wednesday: { enabled: false, start: "09:00", end: "18:00", is24_7: false },
          Thursday: { enabled: false, start: "09:00", end: "18:00", is24_7: false },
          Friday: { enabled: false, start: "09:00", end: "18:00", is24_7: false },
          Saturday: { enabled: false, start: "09:00", end: "18:00", is24_7: false },
          Sunday: { enabled: false, start: "09:00", end: "18:00", is24_7: false },
        };
        editingData.availability.forEach((slot: any) => {
          const day: DayOfWeek = slot?.day;
          if (day && next[day]) {
            next[day] = {
              enabled: Boolean(slot?.isOpen),
              start: slot?.isOpen
                ? minutesToHHMM(Number(slot?.startTime) || 0)
                : "09:00",
              end: slot?.isOpen
                ? minutesToHHMM(Number(slot?.endTime) || 0)
                : "18:00",
              is24_7: Boolean(slot?.isOpen24Hours),
            };
          }
        });
        setAvailability(next);
      }
      setIs24_7(editingData?.is24_7);
    }
  }, [editingData]);

  // Validate availability times
  const validateAvailabilityTimes = (): string | null => {
    for (const day of Object.keys(availability) as DayOfWeek[]) {
      const a = availability[day];
      if (a.enabled) {
        const start = timeToMinutes(a.start);
        const end = timeToMinutes(a.end);
        if (start < 0 || end < 0) {
          return `${day}: Invalid time format`;
        }
        if (start >= end && !is24_7) {
          return `${day}: Start time must be earlier than end time`;
        }
      }
    }
    return null;
  };

  // Function to handle continue button with validation
  const handleContinue = async (
    validateForm: () => Promise<any>,
    setTouched: (touched: any) => void,
    errors: any
  ) => {
    const formErrors = await validateForm();
    if (Object.keys(formErrors).length > 0) {
      // Mark all fields as touched to show validation errors
      const touchedFields = Object.keys(initialValues).reduce((acc, field) => {
        acc[field] = true;
        return acc;
      }, {} as any);
      setTouched(touchedFields);

      showError("Please fix the errors in the form before continuing.");
      return;
    }
    setValueTabs(1);
  };

  const handleImageUpload = async (file: any, setFieldValue: any) => {
    const res = await UploadMedia(file);
    const imageUrl = res?.data?.images?.[0]?.url || res?.data?.image || "";
    if (imageUrl) {
      setFieldValue("image", imageUrl);
    }
  };

  // Add these state variables at the top of your component with other state declarations
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [mapUrl, setMapUrl] = useState<string>(
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d26.84669338315635!2d80.98112951501063!3d26.84669338315635!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjbCsDUwJzQ4LjEiTiA4MMKwNTgnNTIuMSJF!5e0!3m2!1sen!2sin!4v1616580979832!5m2!1sen!2sin"
  );

  useEffect(() => {
    if (userLocation) {
      const { lat, lng } = userLocation;
      const mapUrl = `https://maps.google.com/maps?q=${lat},${lng}&output=embed`;
      // const mapUrl = `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed&z=15&markers=${lat},${lng}`;
      setMapUrl(mapUrl);
    }
  }, [userLocation]);

  return (
    <>
      <div className="main_title">
        <h1>Add Store</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate(-1)}>Store Management</button> -
          {isEdit ? "Edit Store" : "Add Store"}
        </p>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="General Store Info" {...a11yProps(0)} />
              <Tab label="Store Availability" {...a11yProps(1)} />
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting, setFieldError }) => {
            // Validate availability times first
            const availabilityError = validateAvailabilityTimes();
            if (availabilityError) {
              showError(availabilityError);
              setSubmitting(false);
              return;
            }

            try {
              setSubmitting(true);
              // Create the payload exactly as requested
              const payload = {
                image: values.image,
                name: values.name,
                name_ar: values.name_ar,
                storeCode: values.storeCode,
                status: Number(values.status),
                address: values.address,
                city: values.city,
                latitude: Number(values.latitude),
                longitude: Number(values.longitude),
                location: {
                  type: "Point",
                  coordinates: [
                    Number(values.longitude),
                    Number(values.latitude),
                  ],
                },
                orderOnlineLink: values.orderOnlineLink || undefined,
                storeManagerContact: values.storeManagerContact || undefined,
                is24_7: is24_7,
                availability: (Object.keys(availability) as DayOfWeek[]).map(
                  (day) => {
                    const a = availability[day];
                    return {
                      day,
                      isOpen: a.enabled,
                      startTime: a.enabled ? timeToMinutes(a.start) : 0,
                      endTime: a.enabled ? timeToMinutes(a.end) : 0,
                    };
                  }
                ),
              };

              if (isEdit) {
                await updateStore({
                  body: { ...payload } as any,
                  id: routeId || "",
                }).unwrap();
              } else {
                await addStore(payload as any).unwrap();
              }
              navigate("/manage-stores");
            } catch (error: any) {
              console.error("Error submitting store:", error);
              showError(
                error?.data?.message ||
                "Failed to save store. Please try again."
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            isSubmitting,
            validateForm,
            setTouched,
          }) => {
            // Keep latest setFieldValue accessible to Places autocomplete effect
            setFieldValueRef.current = setFieldValue;

            return (
              <Form className="form">
                <CustomTabPanel value={valueTabs} index={0}>
                  <div className="gap_p">
                    {/* Store Image */}
                    <div className="control_group w_100">
                      <div className="gap_p">
                        <div className="control_group w_33">
                          <label htmlFor="icon-button-file">
                            Upload Image <span style={{ color: "red" }}>*</span>
                          </label>
                          <p
                            style={{
                              fontSize: "12px",
                              color: "#666",
                              marginTop: "5px",
                              marginBottom: "8px",
                              fontWeight: "normal",
                            }}
                          >
                            For better visualization please add images in: 1080
                            × 1080 px to 1920 × 1080 px
                          </p>
                          {values.image ? (
                            <div className="upload_image v2 no_radius">
                              <div className="upload_image_holder">
                                <figure>
                                  <CardMedia
                                    component="img"
                                    image={values.image}
                                    alt="Uploaded promotion image"
                                  />
                                </figure>
                                <IconButton
                                  aria-label="Remove uploaded image"
                                  onClick={() => {
                                    setFieldValue("image", "");
                                    setFileName("");
                                  }}
                                  size="small"
                                >
                                  <CloseIcon />
                                </IconButton>
                              </div>
                            </div>
                          ) : (
                            <label
                              className="upload_image v2 no_radius"
                              htmlFor="icon-button-file"
                            >
                              <Input
                                sx={{ display: "none" }}
                                id="icon-button-file"
                                type="file"
                                inputProps={{
                                  accept: "image/png,image/jpeg,image/jpg",
                                }}
                                onChange={(
                                  event: ChangeEvent<HTMLInputElement>
                                ) => {
                                  const files = (
                                    event.target as HTMLInputElement
                                  ).files;
                                  if (files && files[0]) {
                                    if (files[0].type.includes("image")) {
                                      // Validate file size (max 5MB)
                                      if (files[0].size > 5 * 1024 * 1024) {
                                        showError(
                                          "Image size must be less than 5MB."
                                        );
                                        return;
                                      }
                                      setFileName(files[0].name);
                                      handleImageUpload(
                                        files[0],
                                        setFieldValue
                                      );
                                    } else {
                                      showError(
                                        "This field only accepts images (PNG, JPEG, JPG)."
                                      );
                                    }
                                  }
                                }}
                              />
                              <span className="upload_image_holder">
                                <figure className="add_img">
                                  <img
                                    src="/static/images/placeholder.png"
                                    alt="placeholder"
                                  />
                                  <span className="upload_img_icon">
                                    <img
                                      src="/static/images/edit2_icon.svg"
                                      alt="Upload"
                                    />
                                  </span>
                                </figure>
                              </span>
                            </label>
                          )}
                          {touched.image && errors.image && (
                            <FormHelperText error>
                              {String(errors.image)}
                            </FormHelperText>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Store Name */}
                    <div className="control_group w_33">
                      <label htmlFor="store_name">
                        Store Name <span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        id="store_name"
                        name="name"
                        placeholder="Enter Store Name"
                        fullWidth
                        hiddenLabel
                        value={values.name}
                        onChange={handleChange}
                        error={touched.name && Boolean(errors.name)}
                        helperText={
                          touched.name && errors.name ? String(errors.name) : ""
                        }
                      />
                    </div>

                    <div className="control_group w_33">
                      <label htmlFor="store_name_ar">
                        Store Name (Arabic){" "}
                        <span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        id="store_name_ar"
                        name="name_ar"
                        placeholder="Enter Store Name (Arabic)"
                        fullWidth
                        hiddenLabel
                        value={values.name_ar}
                        onChange={handleChange}
                        error={touched.name_ar && Boolean(errors.name_ar)}
                        helperText={
                          touched.name_ar && errors.name_ar
                            ? String(errors.name_ar)
                            : ""
                        }
                      />
                    </div>

                    {/* Store Code */}
                    <div className="control_group w_33">
                      <label htmlFor="store_code">
                        Store Code (Internal Reference){" "}
                        <span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        id="store_code"
                        name="storeCode"
                        placeholder="Enter Store Code (e.g., STR001)"
                        fullWidth
                        hiddenLabel
                        value={values.storeCode.toUpperCase()}
                        onChange={(e) =>
                          setFieldValue(
                            "storeCode",
                            e.target.value.toUpperCase()
                          )
                        }
                        error={touched.storeCode && Boolean(errors.storeCode)}
                        helperText={
                          touched.storeCode && errors.storeCode
                            ? String(errors.storeCode)
                            : ""
                        }
                      />
                    </div>

                    {/* Status */}
                    <div className="control_group w_33">
                      <label htmlFor="status">
                        Status <span style={{ color: "red" }}>*</span>
                      </label>
                      <Select
                        id="status"
                        name="status"
                        fullWidth
                        value={String(values.status)}
                        onChange={(e: any) =>
                          setFieldValue("status", Number(e.target.value))
                        }
                        error={touched.status && Boolean(errors.status)}
                      >
                        <MenuItem value={1}>Open</MenuItem>
                        <MenuItem value={0}>Closed</MenuItem>
                      </Select>
                      {touched.status && errors.status && (
                        <FormHelperText error>
                          {String(errors.status)}
                        </FormHelperText>
                      )}
                    </div>

                    {/* Address */}
                    <div className="control_group w_66">
                      <label htmlFor="store_address">
                        Address <span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        id="store_address"
                        name="address"
                        placeholder="Enter Store Address"
                        fullWidth
                        hiddenLabel
                        value={values.address}
                        onChange={handleChange}
                        inputRef={addressInputRef}
                        error={touched.address && Boolean(errors.address)}
                        helperText={
                          touched.address && errors.address
                            ? String(errors.address)
                            : ""
                        }
                      />
                    </div>

                    {/* Region/City */}
                    <div className="control_group w_33">
                      <label htmlFor="city">
                        City <span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        id="city"
                        name="city"
                        placeholder="City will be auto-filled from address"
                        fullWidth
                        hiddenLabel
                        value={values.city}
                        onChange={handleChange}
                        // InputProps={{ readOnly: true }}
                        error={touched.city && Boolean(errors.city)}
                        helperText={
                          touched.city && errors.city ? String(errors.city) : ""
                        }
                      />
                    </div>

                    {/* Coordinates Display */}
                    <div className="control_group w_66 flex gap_16">
                      <div className="control_group w_66">
                        <label>
                          Latitude <span style={{ color: "red" }}>*</span>
                        </label>
                        <TextField
                          placeholder="Enter latitude"
                          value={values.latitude}
                          fullWidth
                          hiddenLabel
                          size="small"
                          onChange={(e) => {
                            const val = e.target.value;
                            // Allow numbers, decimal points, and minus sign
                            if (val === "" || /^-?\d*\.?\d*$/.test(val)) {
                              setFieldValue("latitude", val);
                              // Update map when latitude changes
                              if (val && values.longitude) {
                                setUserLocation({
                                  lat: parseFloat(val),
                                  lng: parseFloat(values.longitude),
                                });
                              }
                            }
                          }}
                          onBlur={(e) => {
                            // Validate latitude range on blur
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && (val < -90 || val > 90)) {
                              // Optionally show a toast or handle invalid input
                              console.warn("Invalid latitude value:", val);
                            }
                          }}
                          error={touched.latitude && Boolean(errors.latitude)}
                          helperText={
                            touched.latitude && errors.latitude
                              ? String(errors.latitude)
                              : ""
                          }
                        />
                      </div>

                      <div className="control_group w_66">
                        <label>
                          Longitude <span style={{ color: "red" }}>*</span>
                        </label>
                        <TextField
                          placeholder="Enter longitude"
                          value={values.longitude}
                          fullWidth
                          hiddenLabel
                          size="small"
                          onChange={(e) => {
                            const val = e.target.value;
                            // Allow numbers, decimal points, and minus sign
                            if (val === "" || /^-?\d*\.?\d*$/.test(val)) {
                              setFieldValue("longitude", val);
                              // Update map when longitude changes
                              if (values.latitude && val) {
                                setUserLocation({
                                  lat: parseFloat(values.latitude),
                                  lng: parseFloat(val),
                                });
                              }
                            }
                          }}
                          onBlur={(e) => {
                            // Validate longitude range on blur
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && (val < -180 || val > 180)) {
                              console.warn("Invalid longitude value:", val);
                            }
                          }}
                          error={touched.longitude && Boolean(errors.longitude)}
                          helperText={
                            touched.longitude && errors.longitude
                              ? String(errors.longitude)
                              : ""
                          }
                        />
                      </div>
                    </div>
                    {/* Google Map Embed */}
                    <div className="control_group w_100">
                      <label htmlFor="map">Map Location</label>
                      {locationError && (
                        <div
                          className="error-message"
                          style={{ color: "red", marginBottom: "10px" }}
                        >
                          {locationError}
                        </div>
                      )}
                      <iframe
                        title="map"
                        src={mapUrl}
                        width="100%"
                        height="250"
                        style={{
                          border: 0,
                          borderRadius: "10px",
                          display: "flex",
                          overflow: "hidden",
                        }}
                        allowFullScreen
                        loading="lazy"
                        key={mapUrl}
                      ></iframe>
                    </div>

                    {/* Coordinates */}
                    {/* Order Online Link */}
                    <div className="control_group w_50">
                      <label htmlFor="order_link">
                        Order Online Link (Optional)
                      </label>
                      <TextField
                        id="order_link"
                        name="orderOnlineLink"
                        placeholder="https://example.com/order"
                        fullWidth
                        hiddenLabel
                        value={values.orderOnlineLink}
                        onChange={handleChange}
                        error={
                          touched.orderOnlineLink &&
                          Boolean(errors.orderOnlineLink)
                        }
                        helperText={
                          touched.orderOnlineLink && errors.orderOnlineLink
                            ? String(errors.orderOnlineLink)
                            : ""
                        }
                      />
                    </div>

                    {/* Store Manager Contact */}
                    <div className="control_group w_50">
                      <label htmlFor="manager_contact">
                        Store Manager Contact (Optional)
                      </label>
                      <TextField
                        id="manager_contact"
                        name="storeManagerContact"
                        placeholder="+971501234567"
                        fullWidth
                        hiddenLabel
                        value={values.storeManagerContact}
                        onChange={handleChange}
                        error={
                          touched.storeManagerContact &&
                          Boolean(errors.storeManagerContact)
                        }
                        helperText={
                          touched.storeManagerContact &&
                            errors.storeManagerContact
                            ? String(errors.storeManagerContact)
                            : ""
                        }
                      />
                    </div>
                  </div>
                  <div className="form_btn">
                    <Button
                      className="btn btn_primary"
                      type="button"
                      onClick={() =>
                        handleContinue(validateForm, setTouched, errors)
                      }
                    >
                      Continue
                    </Button>
                  </div>
                </CustomTabPanel>

                <CustomTabPanel value={valueTabs} index={1}>
                  <div className="section_title">
                    <h2>Weekly Availability</h2>
                    <p>Set the operating hours for each day of the week</p>
                  </div>
                  <div className="control_group flex w_33">
                    <SwitchToggle
                      checked={is24_7}
                      onChange={(checked) => toggle24Hours(checked)}
                    />
                    <label htmlFor={`is24_7${is24_7}`}>Open 24/7</label>
                  </div>
                  {/* Availability Toggle */}
                  {(Object.keys(availability) as DayOfWeek[]).map((day) => (
                    <div key={day} className="availability_item">
                      <div className="gap_p aic">
                        <div className="control_group w_33">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={availability[day].enabled}
                                onChange={() => toggleAvailability(day)}
                                disabled={is24_7}
                              />
                            }
                            label={day}
                          />
                        </div>
                        {availability[day].enabled && (
                          <div className="control_group w_66">
                            <div className="gap_p flex">
                              <div className="control_group flex w_50">
                                <label htmlFor={`startTime_${day}`}>
                                  Start Time
                                </label>
                                <TextField
                                  type="time"
                                  id={`startTime_${day}`}
                                  value={availability[day].start}
                                  onChange={(e) =>
                                    updateTime(day, "start", e.target.value)
                                  }
                                  InputLabelProps={{ shrink: true }}
                                  size="small"
                                  inputProps={{
                                    step: 300, // 5-minute intervals
                                  }}
                                  disabled={is24_7}
                                />
                              </div>
                              <div className="control_group flex w_50">
                                <label htmlFor={`endTime_${day}`}>
                                  End Time
                                </label>
                                <TextField
                                  type="time"
                                  id={`endTime_${day}`}
                                  value={availability[day].end}
                                  onChange={(e) =>
                                    updateTime(day, "end", e.target.value)
                                  }
                                  InputLabelProps={{ shrink: true }}
                                  size="small"
                                  inputProps={{
                                    step: 300, // 5-minute intervals
                                  }}
                                  disabled={is24_7}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="form_btn">
                    <Button
                      className="btn btn_secondary"
                      type="button"
                      onClick={() => setValueTabs(0)}
                      style={{ marginRight: "10px" }}
                    >
                      Back
                    </Button>
                    {isAdd && (
                      <Button
                        className="btn btn_primary"
                        type="submit"
                        disabled={isSaving || isSubmitting}
                      >
                        {isEdit
                          ? isSaving || isSubmitting || isUpdating
                            ? "Updating..."
                            : "Update"
                          : isSaving || isSubmitting
                            ? "Submitting..."
                            : "Submit"}
                      </Button>
                    )}
                  </div>
                </CustomTabPanel>
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
};

export default AddStore;
